// src/features/stock/hooks/useStock.js
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import stockApi from '../services/stockApi';

export const useStock = (initialParams = {}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [filters, setFilters] = useState(initialParams);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await stockApi.getStockItems({
        page: pagination.currentPage,
        ...filters,
      });
      setItems(response.data.data);
      setPagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
        totalItems: response.data.total,
        perPage: response.data.per_page,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch stock items');
      toast.error('Failed to load stock items');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, filters]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (data) => {
    try {
      const response = await stockApi.createStockItem(data);
      toast.success('Stock item created successfully');
      fetchItems();
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create item');
      throw err;
    }
  };

  const updateItem = async (id, data) => {
    try {
      const response = await stockApi.updateStockItem(id, data);
      toast.success('Stock item updated successfully');
      fetchItems();
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update item');
      throw err;
    }
  };

  const deleteItem = async (id) => {
    try {
      await stockApi.deleteStockItem(id);
      toast.success('Stock item deleted successfully');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete item');
      throw err;
    }
  };

  const bulkDelete = async (ids) => {
    try {
      await stockApi.bulkDeleteStockItems(ids);
      toast.success(`${ids.length} items deleted successfully`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete items');
      throw err;
    }
  };

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const changePage = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return {
    items,
    loading,
    error,
    pagination,
    filters,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    bulkDelete,
    updateFilters,
    changePage,
  };
};